/**
 * Sentry Configuration
 * Error tracking, performance monitoring, and session replay
 */

import * as Sentry from '@sentry/react';

export function initSentry() {
  // Only initialize in production or staging
  if (import.meta.env.DEV) {
    console.log('[Sentry] Skipped in development');
    return;
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN || '',
    environment: import.meta.env.MODE,
    
    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),
      
      // Session replay for debugging
      Sentry.replayIntegration({
        maskAllText: true, // Privacy: mask all text content
        blockAllMedia: true, // Privacy: block all media
      }),
    ],

    // Performance Monitoring
    tracesSampleRate: 0.1, // 10% of transactions for performance
    
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of normal sessions
    replaysOnErrorSampleRate: 1.0, // 100% of error sessions
    
    // Error Filtering
    beforeSend(event, hint) {
      // Filter out benign errors
      const errorMessage = event.exception?.values?.[0]?.value || '';
      
      // Ignore ResizeObserver errors (browser quirk)
      if (errorMessage.includes('ResizeObserver')) {
        return null;
      }
      
      // Ignore network errors from ad blockers
      if (errorMessage.includes('Failed to fetch') && errorMessage.includes('ads')) {
        return null;
      }
      
      // Redact PII from request data
      if (event.request) {
        delete event.request.cookies;
        if (event.request.headers) {
          delete event.request.headers['Authorization'];
          delete event.request.headers['Cookie'];
        }
      }
      
      // Add user context (non-PII only)
      if (event.user) {
        delete event.user.email;
        delete event.user.ip_address;
      }
      
      return event;
    },
    
    // Release tracking (set via build)
    release: import.meta.env.VITE_RELEASE_VERSION,
    
    // Ignore certain URLs
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
    ],
  });

  console.log('[Sentry] Initialized with environment:', import.meta.env.MODE);
}
